import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DarkTrendConfig {
  title:        string;
  value:        number | string;        // número principal grande
  valueLabel?:  string;                 // unidad debajo del valor  ej. "aprobados"
  delta?:       number;                 // diferencia vs período anterior (+12, -3)
  deltaLabel?:  string;                 // ej. "vs ayer"
  trend:        number[];               // valores para el sparkline (mínimo 2)
  accentColor?: string;                 // color del sparkline (default indigo)
}

@Component({
  selector: 'app-dark-trend-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dark-trend-card.component.html',
  styleUrl:    './dark-trend-card.component.scss',
})
export class DarkTrendCardComponent implements OnChanges {
  @Input() config!: DarkTrendConfig;

  // SVG interno (200 × 48 viewBox)
  readonly W = 200;
  readonly H = 48;
  // ID único por instancia para evitar colisiones de <defs> en el mismo DOM
  readonly gradId = `spark-${Math.random().toString(36).slice(2, 7)}`;

  sparkPath   = '';
  areaPath    = '';
  sparkPoints: Array<{ x: number; y: number }> = [];

  get accent(): string {
    return this.config?.accentColor ?? '#6366f1';
  }

  get deltaPositive(): boolean {
    return (this.config?.delta ?? 0) >= 0;
  }

  get deltaAbs(): number {
    return Math.abs(this.config?.delta ?? 0);
  }

  get formattedValue(): string {
    const v = this.config?.value;
    if (v === undefined || v === null) return '—';
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toLocaleString('es-CO');
  }

  ngOnChanges(): void {
    this.buildSparkline();
  }

  private buildSparkline(): void {
    const vals = this.config?.trend ?? [];
    if (vals.length < 2) {
      this.sparkPath = '';
      this.areaPath  = '';
      this.sparkPoints = [];
      return;
    }

    const min   = Math.min(...vals);
    const max   = Math.max(...vals);
    const range = max - min || 1;
    const pad   = 4; // padding vertical en px

    this.sparkPoints = vals.map((v, i) => ({
      x: (i / (vals.length - 1)) * this.W,
      y: pad + (1 - (v - min) / range) * (this.H - pad * 2),
    }));

    const pts = this.sparkPoints;

    // Línea suavizada con curvas cúbicas de Bezier
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const cp1x = pts[i - 1].x + (pts[i].x - pts[i - 1].x) * 0.4;
      const cp2x = pts[i].x     - (pts[i].x - pts[i - 1].x) * 0.4;
      d += ` C ${cp1x} ${pts[i - 1].y} ${cp2x} ${pts[i].y} ${pts[i].x} ${pts[i].y}`;
    }
    this.sparkPath = d;

    // Área bajo la curva (para el relleno semitransparente)
    this.areaPath = `${d} L ${pts[pts.length - 1].x} ${this.H} L ${pts[0].x} ${this.H} Z`;
  }
}
